import {
    WardDto,
    HSFacilityGroup,
    HSFacilityMovement
} from "server-openapi";
import {Entry, IStorage} from '../core/storage/Contract';
import { SyncStreamAPI } from './api';
import {IFacilityGroupSyncService} from './SyncCenter';
import { SyncUtils } from './utils/SyncUtils';
import { DateUtils } from '../core/utils/dateUtils';
import { startOfDay, subDays } from 'date-fns';
import {MemoryCache} from "../core/storage/MemoryCache";
import {parseInt} from "lodash-es";

export interface UpdateFacilityMovementInfoOp {
    type: 'update-facility-movement-info';
    request: {
        facilityMovement: HSFacilityMovement;
        facilityGroupId: number;
    };
}

export class SyncFacilityMovements implements IFacilityGroupSyncService {

    get name(): string {
        return 'SyncFacilityMovements';
    }

    private async isStale(f: WardDto): Promise<boolean> {
        return !!((!f.active && f.lastUpdated && f.lastUpdated < DateUtils.fromDate(subDays(startOfDay(new Date()), 14))));
    }

    private async isFacilityGroup(f: HSFacilityMovement, facilityGroupId: string): Promise<boolean> {
        const facilityGroup = await this.facilityGroupsStore.get(f.movedFrom!.facilityGroupId!.toString());
        return facilityGroup?.hsId?.toString() === facilityGroupId;
    }

    constructor(
        private api: SyncStreamAPI,
        private storage: MemoryCache<HSFacilityMovement>,
        private facilityGroupsStore: IStorage<HSFacilityGroup>,
        private facilitiesStore: IStorage<WardDto>,
        private latestChangeNumbers: IStorage<number | undefined>,
        private epochStore: MemoryCache<string>,
    ) {}

    facilityMovements: HSFacilityMovement[] = [];

    async load(facilityGroupId: string): Promise<void> {
        await this.storage.reset(async (fm) => {
            return await this.isFacilityGroup(fm, facilityGroupId);
        });
    }

    // eslint-disable-next-line sonarjs/cognitive-complexity,max-lines-per-function
    async syncDown(facilityGroupId?: string) {

        const facilitiesToSync = await SyncUtils.getFacilitiesForGroup(facilityGroupId, this.facilitiesStore);

        const changeNumber = await SyncUtils.getChangeNumberForFacilities(
            facilitiesToSync.map((x) => x.hsId!),
            this.latestChangeNumbers,
        );

        // Fetch facility movements for all facilities within facility group
        const facilityMovementData = await this.syncFacilityMovementsDown(
            facilityGroupId!, // non-null assertion on facility group id
            facilitiesToSync.map((facility) => facility.hsId!),
            changeNumber);

        for (const facilityMovement of facilityMovementData) {

            const facility = await this.facilitiesStore.get(facilityMovement.facilityId!.toString()) as WardDto;

            //
            // Is the version of the facility from the in-memory store older than what is on the facilitymovement record we have fetched from the server?
            // If so, then that would mean it has potentially been moved to a new facility group (associated with the facilitymovement) so that what we
            // currently think the facility group it is in now here (based on in-memory store) is now outdated, so we should update their facility group to
            // the new one from the facilitymovement. This will ensure the facility no longer incorrectly shows in the current facility group on the screen
            // when it shouldn't. Also, if the DoseInterface flag on the facility has now been turned off (which is captured in the facilitymovement
            // record's newSyncStream), then that means the facility has been set to no longer be used in medisphere so it should also get removed here.
            //

            if (facility
                && (!facilityMovement.facilityVersion || facility.version! < facilityMovement.facilityVersion!)) {

                if((facilityMovement.movedTo && facilityMovement.movedTo.facilityGroupId!.toString() !== facilityGroupId)
                    || facilityMovement.newSyncStream === false) {

                    // First handle if the facility no longer has medishpere integration functionality enabled.
                    if(facilityMovement.newSyncStream === false){
                        await this.facilitiesStore.delete(facilityMovement.facilityId!.toString())
                        continue;
                    }

                    // Does the in-memory store of facility groups already contain an entry for the facility group that the facility has moved to? If not, then
                    // it's either a facility group not available to the current user or it's one not used in medisphere, so facility would need to be removed.

                    // Grab the target facility group from local store
                    const targetFacilityGroup = await this.facilityGroupsStore.get(facilityMovement.movedTo!.facilityGroupId!.toString());

                    if(targetFacilityGroup){
                        const updatedFacility = { ...facility, facilityGroupId: facilityMovement?.movedTo?.facilityGroupId };
                        await this.facilitiesStore.set(facility.hsId!.toString(), updatedFacility);
                    } else {
                        await this.facilitiesStore.delete(facilityMovement.facilityId!.toString())
                    }
                }
                else {
                    await this.facilitiesStore.delete(facilityMovement.facilityId!.toString())
                }
            }
        }

        await this.storage.setMany(
            facilityMovementData.map((facilityMovement) => ({
                key: this.storage.get_key!(facilityMovement),
                value: facilityMovement,
            }))
        );

        await SyncUtils.setChangeNumberForFacilities(
            facilitiesToSync.map((facility) => facility.hsId!),
            this.latestChangeNumbers,
            facilityMovementData
        );
    }

    private async syncFacilityMovementsDown(
        facilityGroupId: string,
        facilityIds: number[],
        changeNumber: number,
    ): Promise<HSFacilityMovement[]> {

        const pageSize = 200;
        const facilityMovements = await this.api.facilityMovements.facilityMovementsListFacilityMovements(
            parseInt(facilityGroupId),
            changeNumber,
            pageSize,
        );

        this.facilityMovements = facilityMovements.data;

        if (facilityMovements.data.length === pageSize) {
            return [
                ...facilityMovements.data,
                ...(await this.syncFacilityMovementsDown(facilityGroupId, facilityIds, SyncUtils.getLatestChangeNumber(facilityMovements.data)!)),
            ];
        }

        return facilityMovements.data;
    }

    async clear() {
        await this.storage.clear();
        await this.latestChangeNumbers.clear();
    }

    isAllowed(canUserAccessMedication: boolean): boolean {
        // Only if you can view a round.
        return canUserAccessMedication;
    }

    async archive(): Promise<string[]> {
        const keysToDelete: string[] = [];

        for (const [k, v] of (await this.storage.all()).entries()) {
            if (await this.isStale(v)) {
                keysToDelete.push(k)
            }
        }

        await this.storage.deleteMany(keysToDelete);

        return keysToDelete;
    }

    setEncryptionVersion(version: number): void {
        this.storage.compressOnSave = (version > 1);
    }

    async rewrite(): Promise<void> {
        const entries: Entry<WardDto>[] = [...(await this.storage.all())].map((keyValueArray) => {
            return {
                key: keyValueArray[0],
                value: keyValueArray[1]
            };
        });

        return await this.storage.setMany(entries);
    }
}
